import React, { useState } from "react";
// 🎯 DIRECT IMPORTS: Logic is now decoupled from the Context
import { registerNewBatch, deliverFiles } from "../../services/batchActions"; 
import { stageFilesLocally, finalizeLocalBatch } from "../../services/storageService";
import { useBatch } from "../../context/BatchContext"; 

export default function SmartPicker({ 
  MetadataComponent, 
  siteMetaData = {}, 
  setSiteMetaData, 
  batchType = 1 
}) {
  // Pull only UI/Telemetry helpers from Context
  const { refreshAllBatches, handleProgress } = useBatch(); 
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isStaging, setIsStaging] = useState(false);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStart = async () => {
    if (selectedFiles.length === 0 || isStaging) return;
    const filesToDeliver = [...selectedFiles];
    const siteKey = process.env.REACT_APP_SITE_KEY; 
    setIsStaging(true);

    try {
      // 1. Handshake: SQL Backend (Direct Service Call)
      const newBatchObj = await registerNewBatch(
        filesToDeliver,
        { siteKey: siteKey, batchType },
        siteMetaData 
      );

      // 2. Local Storage (Dexie)
      await stageFilesLocally(filesToDeliver, newBatchObj.batchId, siteKey);
      await finalizeLocalBatch(
        newBatchObj.batchId,
        siteKey,
        newBatchObj.hash,
        newBatchObj.timestamp,
        siteMetaData 
      );

      // 3. Cleanup UI
      setSelectedFiles([]);
      
      // 4. Trigger Delivery & Provide Telemetry Callback
      // We pass handleProgress so the QueueManager can update animated bars
      deliverFiles(newBatchObj.batchId, filesToDeliver, siteKey, handleProgress);
      
      // 5. Sync the Monitor View
      await refreshAllBatches();

    } catch (err) {
      console.error("🚨 SMART_PICKER_FAILURE:", err);
    } finally {
      setIsStaging(false);
    }
  };

  return (
    <div className="card shadow-sm border-0 mb-4 bg-light p-4">
      {isStaging ? (
        <div className="p-5 text-center bg-white rounded border">
          <div className="spinner-border text-primary mb-3"></div>
          <p className="fw-bold mb-0 text-uppercase small">Finalizing Storage...</p>
        </div>
      ) : (
        <div className="p-5 border rounded bg-white text-center shadow-sm" style={{ borderStyle: "dashed", borderWidth: "2px", borderColor: "#dee2e6" }}>
          <input type="file" multiple id="smart-file-input" className="d-none" onChange={handleFileChange} />
          <label htmlFor="smart-file-input" style={{ cursor: "pointer", width: "100%" }}>
             <img src="/icons/2D2C31_document.png" alt="upload" style={{ width: "40px" }} className="mb-2" />
             <p className="mb-0 small fw-bold text-uppercase">Select Files</p>
          </label>
        </div>
      )}

      {selectedFiles.length > 0 && !isStaging && (
        <div className="mt-4 pt-3 border-top">
          {MetadataComponent && (
            <MetadataComponent metadata={siteMetaData} setMetadata={setSiteMetaData} />
          )}

          <div className="list-group mb-3">
            {selectedFiles.map((f, i) => (
              <div key={i} className="list-group-item d-flex justify-content-between align-items-center p-2 border-0 mb-1 rounded">
                <span className="small text-truncate">{f.name}</span>
                <button className="btn btn-sm btn-link text-danger fw-bold" onClick={() => removeFile(i)}>&times;</button>
              </div>
            ))}
          </div>

          <button className="btn btn-dark w-100 fw-bold py-3 shadow" onClick={handleStart}>
            INITIATE SECURE BATCH DELIVERY
          </button>
        </div>
      )}
    </div>
  );
}